import { useEffect, useState, useCallback, useRef } from 'react'
import { useAdmin, type AuditEvent } from '@/hooks/useAdmin'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { PaginationControls } from '@/components/ui/pagination-controls'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ScrollText, Search } from 'lucide-react'

const PAGE_SIZE = 50

function actionBadgeClass(action: string) {
  if (action.startsWith('create')) return 'bg-primary/10 text-primary border-primary/30'
  if (action.startsWith('delete') || action.startsWith('remove'))
    return 'bg-destructive/10 text-destructive border-destructive/30'
  if (action.startsWith('update') || action.startsWith('patch'))
    return 'bg-muted/60 text-foreground border-border'
  return 'text-muted-foreground border-border'
}

function DetailsPopover({ details }: { details: Record<string, unknown> | null }) {
  if (!details || Object.keys(details).length === 0) return <span className="text-muted-foreground/50">—</span>
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="text-xs underline underline-offset-2 text-muted-foreground hover:text-foreground transition-colors">
          view
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-96 max-h-72 overflow-y-auto p-3">
        <pre className="text-[11px] font-mono text-foreground whitespace-pre-wrap break-words">
          {JSON.stringify(details, null, 2)}
        </pre>
      </PopoverContent>
    </Popover>
  )
}

export function AuditPage() {
  const { listAudit } = useAdmin()

  const [events, setEvents] = useState<AuditEvent[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)

  const [orgId, setOrgId] = useState('')
  const [userId, setUserId] = useState('')
  const [action, setAction] = useState('')
  const [entityType, setEntityType] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const load = useCallback(
    (p: number, filters: { orgId: string; userId: string; action: string; entityType: string; from: string; to: string }) => {
      setLoading(true)
      setError(null)
      listAudit({
        organizationId: filters.orgId || undefined,
        userId: filters.userId || undefined,
        action: filters.action || undefined,
        entityType: filters.entityType || undefined,
        from: filters.from || undefined,
        to: filters.to || undefined,
        limit: PAGE_SIZE,
        offset: (p - 1) * PAGE_SIZE,
      })
        .then(({ events: e, total: t }) => {
          setEvents(e)
          setTotal(t)
        })
        .catch(() => setError('Failed to load audit log'))
        .finally(() => setLoading(false))
    },
    [listAudit],
  )

  const currentFilters = useRef({ orgId, userId, action, entityType, from, to })
  currentFilters.current = { orgId, userId, action, entityType, from, to }

  useEffect(() => {
    load(page, currentFilters.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  const debouncedLoad = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setPage(1)
      load(1, currentFilters.current)
    }, 350)
  }

  const handleFilter = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setter(e.target.value)
    debouncedLoad()
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <div className="space-y-6">
      <div>
        <h1 className="brand-wordmark text-2xl">Audit Log</h1>
        <p className="kicker text-muted-foreground mt-1">
          {total.toLocaleString()} event{total !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[160px] max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <Input
            value={orgId}
            onChange={handleFilter(setOrgId)}
            placeholder="Org ID…"
            className="pl-8 h-9 text-sm"
          />
        </div>
        <Input
          value={userId}
          onChange={handleFilter(setUserId)}
          placeholder="User ID…"
          className="h-9 text-sm w-48"
        />
        <Input
          value={action}
          onChange={handleFilter(setAction)}
          placeholder="Action…"
          className="h-9 text-sm w-40"
        />
        <Input
          value={entityType}
          onChange={handleFilter(setEntityType)}
          placeholder="Entity type…"
          className="h-9 text-sm w-40"
        />
        <Input
          type="date"
          value={from}
          onChange={handleFilter(setFrom)}
          className="h-9 text-sm w-40"
          title="From date"
        />
        <Input
          type="date"
          value={to}
          onChange={handleFilter(setTo)}
          className="h-9 text-sm w-40"
          title="To date"
        />
      </div>

      {/* Table */}
      <Card>
        {error ? (
          <CardContent className="p-6 text-sm text-destructive">{error}</CardContent>
        ) : loading ? (
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {Array.from({ length: 10 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 px-6 py-3.5">
                  <div className="h-4 w-32 rounded bg-muted/40 animate-pulse" />
                  <div className="h-4 w-24 rounded bg-muted/30 animate-pulse" />
                  <div className="ml-auto h-4 w-48 rounded bg-muted/30 animate-pulse" />
                </div>
              ))}
            </div>
          </CardContent>
        ) : events.length === 0 ? (
          <CardContent className="flex flex-col items-center gap-3 py-16 text-muted-foreground">
            <ScrollText className="h-8 w-8 opacity-40" />
            <p className="text-sm">No audit events found</p>
          </CardContent>
        ) : (
          <CardContent className="p-0">
            <div className="grid grid-cols-[150px_120px_120px_1fr_1fr_60px] gap-3 px-6 py-2.5 border-b text-xs font-medium text-muted-foreground uppercase tracking-wide">
              <span>Time</span>
              <span>Action</span>
              <span>Entity type</span>
              <span>User</span>
              <span>Org</span>
              <span className="text-center">Details</span>
            </div>
            <div className="divide-y divide-border">
              {events.map((ev) => (
                <div
                  key={ev.id}
                  className="grid grid-cols-[150px_120px_120px_1fr_1fr_60px] gap-3 px-6 py-3 items-center text-sm hover:bg-accent/20 transition-colors"
                >
                  <p className="mono-accent text-muted-foreground text-xs">
                    {new Date(ev.created_at).toLocaleString()}
                  </p>
                  <div>
                    <Badge variant="outline" className={`text-xs ${actionBadgeClass(ev.action)}`}>
                      {ev.action}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{ev.entity_type || '—'}</p>
                  <div className="min-w-0">
                    <p className="text-sm truncate">{ev.user_email || '—'}</p>
                    <p className="mono-accent text-muted-foreground truncate">{ev.user_id}</p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm truncate">{ev.org_name || '—'}</p>
                    <p className="mono-accent text-muted-foreground truncate">{ev.organization_id}</p>
                  </div>
                  <div className="flex justify-center">
                    <DetailsPopover details={ev.details} />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        )}
      </Card>

      {!loading && !error && total > 0 && (
        <PaginationControls
          page={page}
          totalPages={totalPages}
          pageSize={PAGE_SIZE}
          total={total}
          onPageChange={setPage}
        />
      )}
    </div>
  )
}
